import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormBuilder,
  Separator,
  useToast,
  Button,
  buildPayloadFromFields,
} from '@folio/components';
import { getResolver } from '@folio/components/forms/form-builder/getZodResolverFromFields';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  CreateActiveTeamInputBody,
  distributionRequestStatusEnum,
  legacyPermissionsControllerAtMeQueryOptions,
  useInvitationControllerBrokerInvite,
  useViewSpecificControllerCreateActiveTeam,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { Field, useKorintConfig, useKorintModuleConfig } from 'korint-sdk/react';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

interface TeamCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function TeamCreationDialog({ isOpen, onClose, onSuccess }: TeamCreationDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const teamManagementConfig = useKorintModuleConfig('teamManagement');

  const {
    korintConfig: { productsConfig },
  } = useKorintConfig();

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: parentBrokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  // RETAIL firms can only invite commercials on products they've been approved to distribute.
  // For WHOLESALE, the dialog isn't gated by distribution requests — all tenant products remain available.
  const isRetail = parentBrokerageFirm?.firmType === 'RETAIL';
  const { data: approvedProductIds } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(
      user?.brokerageFirmId!,
      {
        statuses: [distributionRequestStatusEnum.APPROVED],
      },
    ),
    enabled: !!user?.brokerageFirmId && isRetail,
    select: data => data.data.map(d => d.productId),
  });

  const { teamFields, leaderFields } = useMemo(() => {
    const allEntries = Object.entries(productsConfig ?? {});
    const filteredEntries = isRetail
      ? allEntries.filter(([id]) => approvedProductIds?.includes(id))
      : allEntries;
    const productsOptions = filteredEntries.map(([id]) => ({
      value: id,
      label: t(`products.${id}.label`),
    }));

    if (!productsOptions.length) {
      return { teamFields: [] as Field[], leaderFields: [] as Field[] };
    }

    const productsField: Field = {
      name: 'teamProductsSelect',
      type: 'multiSelect',
      required: true,
      options: productsOptions,
      formDataAccessor: data => {
        return { productIds: data.teamProductsSelect };
      },
    };

    return {
      teamFields: [
        productsField,
        ...((teamManagementConfig?.teamCreationTeamFields ?? []) as Field[]),
      ],
      leaderFields: (teamManagementConfig?.teamCreationFirstBrokerFields ?? []) as Field[],
    };
  }, [productsConfig, teamManagementConfig, t, isRetail, approvedProductIds]);

  const fields = useMemo(() => [...teamFields, ...leaderFields], [teamFields, leaderFields]);

  const form = useForm({
    resolver: getResolver(fields),
    defaultValues: {
      teamProductsSelect: [],
    },
  });

  const { mutateAsync: inviteBroker, isPending: isInviting } =
    useInvitationControllerBrokerInvite();

  const { mutateAsync: createActiveTeam, isPending: isCreating } =
    useViewSpecificControllerCreateActiveTeam({
      mutation: {
        onSuccess: async result => {
          toast.toast({
            title: t('teamManagement.toastSuccessTitle'),
            description: t('teamManagement.creationDialog.createSuccess'),
          });
          if (
            teamManagementConfig?.sendInviteOnCreation &&
            teamManagementConfig?.inviteCustomFieldMapping &&
            user?.brokerageFirmId &&
            parentBrokerageFirm
          ) {
            const mapping = teamManagementConfig.inviteCustomFieldMapping;
            const formData = form.getValues();

            const getFormValue = (fieldName: string): string | undefined => {
              const value = formData[fieldName as keyof typeof formData];
              return typeof value === 'string' ? value : undefined;
            };

            const email = getFormValue(mapping.email);
            const firstName = getFormValue(mapping.firstName);
            const lastName = getFormValue(mapping.lastName);

            if (email && firstName && lastName) {
              const rolesForFirmType =
                teamManagementConfig.inviteRole?.[
                  parentBrokerageFirm.firmType as keyof typeof teamManagementConfig.inviteRole
                ];

              // Support both string (legacy) and array (new) formats
              const availableRoles = Array.isArray(rolesForFirmType)
                ? rolesForFirmType
                : rolesForFirmType
                  ? [rolesForFirmType]
                  : [];

              // Use first available role (for auto-creation, we pick the first one)
              const inviteRole = availableRoles[0];

              if (inviteRole) {
                try {
                  await inviteBroker({
                    data: {
                      email,
                      firstName,
                      lastName,
                      brokerRole: inviteRole,
                      brokerageFirmId: result.brokerageFirmId,
                    },
                  });
                  toast.toast({
                    title: t('teamManagement.toastSuccessTitle'),
                    description: t('teamManagement.creationDialog.inviteSuccess'),
                  });
                } catch (error: any) {
                  toast.toast({
                    title: t('teamManagement.toastErrorTitle'),
                    description: error.userError || t('teamManagement.creationDialog.inviteError'),
                    variant: 'destructive',
                  });
                }
              }
            }
          }

          form.reset();
          onSuccess();
        },
        onError: error => {
          toast.toast({
            title: t('teamManagement.toastErrorTitle'),
            description: error.userError || t('teamManagement.creationDialog.createError'),
          });
        },
      },
    });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('teamManagement.creationDialog.title')}</DialogTitle>
        </DialogHeader>

        <Stack>
          <Stack gap={2}>
            <Text size="sm" weight="medium">
              {t('teamManagement.creationDialog.teamSectionTitle')}
            </Text>
            <FormBuilder
              fields={teamFields}
              form={form as any}
              isLoading={isCreating}
              portalTarget={null}
            />
          </Stack>

          {leaderFields.length > 0 && (
            <>
              <Separator />

              <Stack gap={2}>
                <Text size="sm" weight="medium">
                  {t('teamManagement.creationDialog.leaderSectionTitle')}
                </Text>
                <FormBuilder
                  fields={leaderFields}
                  form={form as any}
                  isLoading={isCreating}
                  portalTarget={null}
                />
              </Stack>
            </>
          )}

          <Group align="stretch" justify="end" gap={2}>
            <Button variant="outline" onClick={onClose} disabled={isCreating || isInviting}>
              {t('teamManagement.creationDialog.cancel')}
            </Button>
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={form.handleSubmit(async data => {
                if (!user?.brokerageFirmId || !parentBrokerageFirm) return;

                await createActiveTeam({
                  brokerageFirmId: user?.brokerageFirmId,
                  data: {
                    ...(buildPayloadFromFields(fields, data) as CreateActiveTeamInputBody),
                  },
                });
              })}
              loading={isCreating || isInviting}
              disabled={isCreating || isInviting}
            >
              {t('teamManagement.creationDialog.submit')}
            </Button>
          </Group>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
